/**
 * DataFast client helpers — full getting-started integration.
 *
 * 1. Tracking script (index.html) + first-party proxy
 * 2. Stripe revenue attribution via cookies → Checkout metadata
 * 3. Custom goals via window.datafast() (with queue support)
 *
 * @see https://datafa.st/docs/getting-started
 * @see https://datafa.st/docs/custom-goals
 * @see https://datafa.st/docs/stripe-checkout-api
 */

export const DATAFAST_VISITOR_COOKIE = "datafast_visitor_id";
export const DATAFAST_SESSION_COOKIE = "datafast_session_id";

export interface DatafastAttributionPayload {
  datafast_visitor_id?: string;
  datafast_session_id?: string;
}

type DatafastFn = (
  goalOrEvent: string,
  params?: Record<string, string | number | boolean | undefined>,
) => void;

declare global {
  interface Window {
    datafast?: DatafastFn & { q?: unknown[] };
  }
}

function readBrowserCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  try {
    const parts = document.cookie.split(";");
    for (const part of parts) {
      const eq = part.indexOf("=");
      if (eq <= 0) continue;
      const k = part.slice(0, eq).trim();
      if (k !== name) continue;
      const v = part.slice(eq + 1).trim();
      if (!v) return undefined;
      try {
        return decodeURIComponent(v);
      } catch {
        return v;
      }
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

/** Read DataFast cookies for the current visitor (browser only). */
export function getDatafastAttributionIds(): {
  visitorId?: string;
  sessionId?: string;
} {
  const visitorId = readBrowserCookie(DATAFAST_VISITOR_COOKIE);
  const sessionId = readBrowserCookie(DATAFAST_SESSION_COOKIE);
  return {
    ...(visitorId ? { visitorId } : {}),
    ...(sessionId ? { sessionId } : {}),
  };
}

/**
 * Fields to merge into POST bodies for /api/create-subscription-checkout
 * (and other Stripe session creators).
 */
export function getDatafastAttributionPayload(): DatafastAttributionPayload {
  const { visitorId, sessionId } = getDatafastAttributionIds();
  return {
    ...(visitorId ? { datafast_visitor_id: visitorId } : {}),
    ...(sessionId ? { datafast_session_id: sessionId } : {}),
  };
}

/**
 * Merge DataFast IDs into an existing `metadata` object for Stripe sessions.
 */
export function withDatafastMetadata(
  metadata?: Record<string, string | undefined> | null,
): Record<string, string | undefined> {
  const { visitorId, sessionId } = getDatafastAttributionIds();
  return {
    ...(metadata || {}),
    ...(visitorId ? { datafast_visitor_id: visitorId } : {}),
    ...(sessionId ? { datafast_session_id: sessionId } : {}),
  };
}

function sanitizeGoalName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_.:-]/g, "")
    .slice(0, 64);
}

function sanitizeParams(
  params?: Record<string, string | number | boolean | undefined | null>,
): Record<string, string> | undefined {
  if (!params) return undefined;
  const out: Record<string, string> = {};
  let n = 0;
  for (const [k, v] of Object.entries(params)) {
    if (n >= 10) break;
    if (v === null || v === undefined) continue;
    const key = k
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "")
      .slice(0, 64);
    if (!key) continue;
    out[key] = String(v).slice(0, 255);
    n++;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Track a custom goal (client-side). Safe if script not loaded yet when
 * the datafast-queue snippet is present in <head>.
 *
 * Reserved Stripe goals (do NOT use): payment, subscription_started, etc.
 * @see https://datafa.st/docs/custom-goals
 */
export function trackDatafastGoal(
  name: string,
  params?: Record<string, string | number | boolean | undefined | null>,
): void {
  if (typeof window === "undefined") return;
  const goal = sanitizeGoalName(name);
  if (!goal || goal === "identify") return;
  try {
    const fn = window.datafast;
    if (typeof fn !== "function") return;
    const clean = sanitizeParams(params);
    if (clean) fn(goal, clean);
    else fn(goal);
  } catch {
    /* never break UX */
  }
}

/** Membership funnel goals (client). */
export const DatafastGoals = {
  /** User clicked VIP / membership CTA */
  membershipCtaClick: (meta?: {
    plan?: string;
    flow?: string;
    country?: string;
  }) =>
    trackDatafastGoal("membership_cta_click", {
      plan: meta?.plan || "vip",
      flow: meta?.flow,
      country: meta?.country,
    }),
  /** Checkout session request started (before Stripe redirect) */
  initiateCheckout: (meta?: {
    plan?: string;
    flow?: string;
    amount_cents?: number | string;
    currency?: string;
  }) =>
    trackDatafastGoal("initiate_checkout", {
      plan: meta?.plan || "vip",
      flow: meta?.flow,
      amount_cents: meta?.amount_cents,
      currency: meta?.currency || "usd",
    }),
  /** Embedded or hosted checkout UI shown */
  checkoutViewed: (meta?: { plan?: string; mode?: string }) =>
    trackDatafastGoal("checkout_viewed", {
      plan: meta?.plan || "vip",
      mode: meta?.mode,
    }),
  /** Post-pay complete-profile / success */
  membershipSuccess: (meta?: { plan?: string }) =>
    trackDatafastGoal("membership_success_page", {
      plan: meta?.plan || "vip",
    }),
} as const;
